import { apiUrl, sensorsUrl } from "../api/endpoints";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BUCKET_DEVICES } from "../Constants";
import axios from "axios";

const Terminal = ({
  title,
  transparentBackground,
  hint,
  newLine,
  autoScroll,
  value,
  bgColor,
  fgColor,
  draggable,
  pin,
  id,
  tid,
  did,
  dataId,
  deviceId,
  onTerminalCleared,
}) => {
  const [log, setLog] = useState("");
  const [command, setCommand] = useState("");
  const [cursorState, setCursorState] = useState("idle");
  const [isClearing, setIsClearing] = useState(false);
  const [clearFeedback, setClearFeedback] = useState({ type: "idle", message: "" });
  const clearFeedbackTimeoutRef = useRef(null);
  const resolvedBg = bgColor || "var(--background-command)";
  const resolvedFg = fgColor || "rgba(175, 175, 175, 1)";
  const resolvedTitleColor = fgColor || "var(--widget-title-color, rgba(255, 255, 255, 1))";
  const normalizedTitle = String(title ?? "").trim();
  const titleLineCount = normalizedTitle ? normalizedTitle.split(/\r?\n/).length : 0;
  const contentTopOffset = `calc(${titleLineCount > 0 ? 20 + titleLineCount * 24 : 16}px * var(--font-size-multiplier, 1))`;
  const canClearTerminal = !draggable
    && Boolean(String(tid || "").trim())
    && Boolean(String(did || "").trim())
    && Boolean(String(pin || "").trim());
  const titleRightOffset = canClearTerminal
    ? `calc(120px * var(--font-size-multiplier, 1))`
    : draggable
      ? `calc(112px * var(--font-size-multiplier, 1))`
      : `calc(8px * var(--font-size-multiplier, 1))`;
  const resolvedCursor = !draggable
    ? "auto"
    : cursorState === "grabbing"
      ? "grabbing"
      : "grab";

  useEffect(() => {
    setLog(value == null ? "" : String(value));
  }, [value]);

  useEffect(() => () => {
    if (clearFeedbackTimeoutRef.current) {
      clearTimeout(clearFeedbackTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!autoScroll) return;
    const logElement = document.getElementById(`log-${id}`);
    if (logElement) {
      logElement.scrollTop = logElement.scrollHeight;
    }
  }, [autoScroll, id, log]);

  useEffect(() => {
    const input = document.getElementById(`command-${id}`);
    if (!input) return;

    input.style.borderColor = "rgba(60, 195, 223, 0.3)";
    const handleIn = () => (input.style.borderColor = "rgba(60, 195, 223, 0.7)");
    const handleOut = () => (input.style.borderColor = "rgba(60, 195, 223, 0.3)");

    const onFocus = () => {
      handleIn();
      input.removeEventListener("mouseleave", handleOut);
    };
    const onBlur = () => {
      handleOut();
      input.addEventListener("mouseleave", handleOut);
    };

    input.addEventListener("focus", onFocus);
    input.addEventListener("focusout", onBlur);
    input.addEventListener("mouseenter", handleIn);
    input.addEventListener("mouseleave", handleOut);

    return () => {
      input.removeEventListener("focus", onFocus);
      input.removeEventListener("focusout", onBlur);
      input.removeEventListener("mouseenter", handleIn);
      input.removeEventListener("mouseleave", handleOut);
    };
  }, [id, bgColor]);

  const isInteractiveArea = useCallback((target) => {
    if (!target || !(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        [
          "button",
          "a",
          "input",
          "textarea",
          "select",
          "[role='button']",
          "[role='menuitem']",
          "[data-no-drag]",
          ".widget-buttons-container",
          ".widget-buttons-container *",
          ".widget-actions-menu",
          ".widget-actions-menu *",
          ".MuiIconButton-root",
          ".MuiButtonBase-root",
        ].join(","),
      ),
    );
  }, []);

  const pushClearFeedback = useCallback((type, message) => {
    if (clearFeedbackTimeoutRef.current) {
      clearTimeout(clearFeedbackTimeoutRef.current);
      clearFeedbackTimeoutRef.current = null;
    }

    setClearFeedback({ type, message });

    if (type === "idle" || type === "pending") {
      return;
    }

    clearFeedbackTimeoutRef.current = setTimeout(() => {
      setClearFeedback({ type: "idle", message: "" });
      clearFeedbackTimeoutRef.current = null;
    }, 3000);
  }, []);

  const clearTerminal = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canClearTerminal || isClearing) {
      return;
    }

    const confirmed = window.confirm("Clear all saved terminal history for this datastream?");
    if (!confirmed) {
      return;
    }

    setIsClearing(true);
    pushClearFeedback("pending", "Clearing...");

    try {
      await axios.post(
        apiUrl(`data/terminal/clear`),
        {
          bucket: BUCKET_DEVICES,
          tid: String(tid).trim(),
          did: String(did).trim(),
          pin: String(pin).trim(),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setLog("");
      try {
        onTerminalCleared?.({
          widgetId: id,
          dataId,
          deviceId,
          did: did == null ? '' : String(did).trim(),
          tid: tid == null ? '' : String(tid).trim(),
          pin: pin == null ? '' : String(pin).trim(),
        });
      } catch (callbackError) {
        console.error("Failed to sync cleared terminal widget state.", callbackError);
      }
      pushClearFeedback("success", "Cleared");
    } catch (error) {
      const message =
        error?.response?.data?.message
        || error?.response?.data?.errors?.[0]?.msg
        || "Failed to clear terminal.";
      pushClearFeedback("error", message);
    } finally {
      setIsClearing(false);
    }
  }, [canClearTerminal, dataId, deviceId, did, id, isClearing, onTerminalCleared, pin, pushClearFeedback, tid]);

  const executeCommand = (e) => {
    if (e.key === "Enter") {
      const newCommand = newLine ? "\n> " + command : " " + command;
      if (did) {
        const body = JSON.stringify({
          device: did,
          template: tid,
          data: { [pin]: newCommand },
        });
        axios
          .post(sensorsUrl("control"), body, {
            headers: { "Content-Type": "application/json" },
          })
          .then((res) => {
            if (res.data === "OK") {
              setLog((prev) => prev + newCommand);
              setCommand("");
            }
          })
          .catch(() => setLog(value));
      }
    }
  };

  const scrollToBottom = (e) => {
    if (autoScroll && e.key === "Enter") {
      const logElement = document.getElementById(`log-${id}`);
      if (logElement) logElement.scrollTop = logElement.scrollHeight;
    }
  };

  return (
    <div
      className="widget-container"
      onMouseDown={(e) => {
        if (!draggable || isInteractiveArea(e.target)) return;
        setCursorState("grabbing");
      }}
      onMouseUp={(e) => {
        if (!draggable || isInteractiveArea(e.target)) return;
        setCursorState("grab");
      }}
      onMouseLeave={() => draggable && setCursorState("grab")}
      draggable={draggable}
      style={{
        cursor: resolvedCursor,
        ...(transparentBackground && {
          backgroundColor: "transparent",
          boxShadow: "none",
        }),
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
      onDragStart={(e) => {
        if (isInteractiveArea(e.target)) {
          e.preventDefault();
          return;
        }

        const wid = e.target.closest(".widget")?.id;
        if (!wid) return;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;
        let dragData = `terminal;${wid};${offsetX};${offsetY};`;
        dragData += wid.startsWith("base") ? "add" : "move";
        e.dataTransfer.setData("text/plain", dragData);
      }}
    >
      <div
        style={{
          position: "absolute",
          top: `calc(12px * var(--font-size-multiplier, 1))`,
          left: `calc(8px * var(--font-size-multiplier, 1))`,
          right: titleRightOffset,
          margin: 0,
          fontFamily: "'Lato', sans-serif",
          fontWeight: 600,
          fontSize: "var(--widget-label-font-size, calc(20px * var(--font-size-multiplier, 1)))",
          lineHeight: "1.2",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          color: resolvedTitleColor,
          zIndex: 10,
          pointerEvents: "none",
        }}
      >
        {title}
      </div>

      {!draggable ? (
        <div
          data-no-drag="true"
          style={{
            position: "absolute",
            top: `calc(12px * var(--font-size-multiplier, 1))`,
            right: `calc(8px * var(--font-size-multiplier, 1))`,
            display: "flex",
            alignItems: "center",
            gap: `calc(8px * var(--font-size-multiplier, 1))`,
            zIndex: 11,
          }}
        >
          {clearFeedback.message ? (
            <span
              aria-live="polite"
              style={{
                maxWidth: `calc(168px * var(--font-size-multiplier, 1))`,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 600,
                fontSize: `calc(11px * var(--font-size-multiplier, 1))`,
                lineHeight: "1.2",
                textAlign: "right",
                color:
                  clearFeedback.type === "error"
                    ? "rgba(255, 143, 143, 1)"
                    : clearFeedback.type === "success"
                      ? "rgba(120, 255, 192, 1)"
                      : "rgba(175, 234, 242, 0.95)",
              }}
            >
              {clearFeedback.message}
            </span>
          ) : null}

          <button
            type="button"
            data-no-drag="true"
            onClick={clearTerminal}
            disabled={!canClearTerminal || isClearing}
            style={{
              minWidth: `calc(72px * var(--font-size-multiplier, 1))`,
              height: `calc(32px * var(--font-size-multiplier, 1))`,
              padding: `0 calc(12px * var(--font-size-multiplier, 1))`,
              borderRadius: `calc(999px * var(--font-size-multiplier, 1))`,
              border: "1px solid rgba(60, 195, 223, 0.35)",
              background: !canClearTerminal
                ? "rgba(27, 42, 61, 0.38)"
                : isClearing
                  ? "rgba(60, 195, 223, 0.18)"
                  : "rgba(16, 27, 46, 0.88)",
              color: !canClearTerminal
                ? "rgba(155, 173, 191, 0.72)"
                : "rgba(220, 247, 255, 0.98)",
              fontFamily: "'Lato', sans-serif",
              fontWeight: 700,
              fontSize: `calc(12px * var(--font-size-multiplier, 1))`,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              cursor: !canClearTerminal || isClearing ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: !canClearTerminal
                ? "none"
                : "0 0 16px rgba(60, 195, 223, 0.12)",
            }}
            title={canClearTerminal ? "Clear saved terminal history" : "Terminal clear is unavailable for this widget"}
          >
            {isClearing ? "Clearing" : "Clear"}
          </button>
        </div>
      ) : null}

      <div
        style={{
          marginTop: contentTopOffset,
          width: "100%",
          height: "100%",
          borderRadius: `calc(8px * var(--font-size-multiplier, 1))`,
          display: "flex",
          flexDirection: "column",
          padding: `calc(16px * var(--font-size-multiplier, 1))`,
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: `calc(8px * var(--font-size-multiplier, 1))`,
          }}
        >
          <textarea
            id={`log-${id}`}
            data-no-drag="true"
            style={{
              width: "100%",
              flexGrow: 1,
              padding: `calc(8px * var(--font-size-multiplier, 1))`,
              border: "1px solid rgba(60, 195, 223, 0.3)",
              borderRadius: `calc(4px * var(--font-size-multiplier, 1))`,
              outline: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "var(--widget-value-font-size, calc(16px * var(--font-size-multiplier, 1)))",
              lineHeight: "100%",
              letterSpacing: "0px",
              color: resolvedFg,
              backgroundColor: resolvedBg,
              resize: "none",
              cursor: "default",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(60, 195, 223, 0.5) rgba(18, 29, 50, 0.3)",
            }}
            value={log?.toString().startsWith("<") ? log : "< " + log}
            readOnly
          />
          <input
            data-no-drag="true"
            value={command}
            type="text"
            style={{
              width: "100%",
              height: `calc(32px * var(--font-size-multiplier, 1))`,
              padding: `0 calc(10px * var(--font-size-multiplier, 1))`,
              border: "1px solid rgba(60, 195, 223, 0.3)",
              borderRadius: `calc(4px * var(--font-size-multiplier, 1))`,
              outline: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: "var(--widget-value-font-size, calc(14px * var(--font-size-multiplier, 1)))",
              lineHeight: "100%",
              color: resolvedFg,
              backgroundColor: resolvedBg,
              transition: "all 0.2s ease",
              boxSizing: "border-box",
            }}
            placeholder={hint || "Type here.."}
            id={`command-${id}`}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => executeCommand(e)}
            onKeyUp={(e) => scrollToBottom(e)}
          />
        </div>
      </div>
    </div>
  );
};

export default Terminal;
